// Minimal structural view of the videoMembers Immutable Map — `has` for the
// registration gate, `getIn` for per-member fields (screenShareActive,
// primaryPeer, peerSetupAttempts). Kept structural (rather than importing
// store types) so the helpers stay pure, dependency-free functions that tests
// can drive with a plain Immutable Map.
export type VideoMembersMap = {
    has(name: string): boolean;
    getIn(path: [string, string]): unknown;
};

/**
 * Decides which rendered member names the visible-tile subscription manager
 * (useVideoSubscriptions) may subscribe to. Extracted from MeetingVideoGrid so
 * the policy is unit-testable in isolation. Two gates:
 *
 * 1. Signaling registration (videoMembers): on join, tiles render from the
 *    REST member list BEFORE `existingParticipants` arrives over the signaling
 *    socket. Subscribing in that window stores a peer that the member's
 *    registration then wipes (VIDEO_ADD_VIDEO_MEMBER replaces the record),
 *    orphaning the connection — and the manager, having fired its subscribe,
 *    never retries: the tile stays on the avatar for good. Gating on
 *    registration makes subscription structurally follow registration. It also
 *    keeps pure dial-in callers — never registered, no video feed — out of the
 *    subscription set.
 *
 * 2. "Show incoming video" (VID-53 AC3, ported from the deleted
 *    useVideoSubscriptionSync): when the viewer turns incoming video off,
 *    remote CAMERA feeds must actually be unsubscribed — a real bandwidth stop
 *    at the SFU (closePrimaryMemberPeer → cancelVideoFrom), not just hidden
 *    pixels (the tile layer hides them separately). Screen shares are exempt:
 *    they ride the sharer's PRIMARY receive peer, so with incoming video off,
 *    only members currently sharing stay subscribable. Toggling back on
 *    restores the full set and the manager re-subscribes the cameras.
 *
 * @param candidateNames display names of the rendered feeds (may contain
 *   empty/undefined entries for feeds without a name — dropped).
 * @param videoMembers the signaling-registered member map, keyed by name.
 * @param showIncomingVideo the viewer's incoming-video toggle; only an
 *   explicit `false` suppresses cameras (undefined means "on", matching the
 *   selector's default).
 */
export const getSubscribableMemberNames = (
    candidateNames: Array<string | undefined>,
    videoMembers: VideoMembersMap,
    showIncomingVideo: boolean | undefined,
): string[] => {
    const incomingVideoOff = showIncomingVideo === false;
    return candidateNames.filter((name): name is string => {
        if (!name || !videoMembers.has(name)) {
            return false;
        }
        return (
            !incomingVideoOff ||
            videoMembers.getIn([name, 'screenShareActive']) === true
        );
    });
};

/**
 * Store-side liveness observation for the subscription manager's self-healing
 * (VideoSubscriptionManager#reportPeerLiveness): is a subscribed member's
 * primary receiver peer to be considered alive right now?
 *
 * "Live" means the slot holds a real WebRtcPeer — the same duck-type check the
 * signaling client uses (isReceiverPeerReady): both placeholder forms (the
 * empty Immutable Map from registration, the plain `{}` written after a close)
 * lack addIceCandidate.
 *
 * Two situations report live even without a real peer, because a dead slot
 * there is NOT an external teardown the manager should heal:
 * - Member no longer registered: departure — the deregistration watcher in
 *   useVideoSubscriptions forgets them; healing would fight it.
 * - peerSetupAttempts > 0: the restart loop (restartPrimaryReceiverPeer) owns
 *   recovery, and its backoff legitimately leaves the slot empty for seconds;
 *   healing here would race a second peer setup against the restart's own.
 *
 * @param videoMembers the signaling-registered member map, keyed by name (null/
 *   undefined — e.g. after teardown — reports live: nothing to heal).
 * @param memberName the subscribed member to observe.
 */
export const isMemberReceiverPeerConsideredLive = (
    videoMembers: VideoMembersMap | null | undefined,
    memberName: string,
): boolean => {
    if (!videoMembers?.has(memberName)) {
        return true;
    }
    const setupAttempts = videoMembers.getIn([memberName, 'peerSetupAttempts']);
    if (typeof setupAttempts === 'number' && setupAttempts > 0) {
        return true;
    }
    const peer = videoMembers.getIn([memberName, 'primaryPeer']) as
        | { addIceCandidate?: unknown }
        | null
        | undefined;
    return typeof peer?.addIceCandidate === 'function';
};
